"use client";

import React, { useMemo, useState } from "react";
import AsyncSelect from "react-select/async";

import { debounce, getUTMParams, replaceHttpWithHttps } from "helpers/Helper";

import { useAppContext } from "@components/Provider";
import Button from "@components/Button/Button";
import NewsletterV2 from "@components/NewsletterV2/NewsletterV2";

import styles from "./BookReview.module.css";
import "./BookReview.css";

const MIN_SEARCH_CHARS = process.env.LDL_MIN_SEARCH_CHARS || 5;
const GOOGLE_API_KEY = process.env.GOOGLE_API_KEY;

const selectedStyle = {
  background: "#2c68b0",
};
const unSelectedStyle = {
  background: "none",
  color: "#000",
  border: "1px solid #ebebeb",
};

const fetchBooks = async (query) => {
  if (query.length < MIN_SEARCH_CHARS) return [];

  const url = `https://www.googleapis.com/books/v1/volumes?q=intitle:${query}&fields=items(id,volumeInfo)&key=${GOOGLE_API_KEY}`;
  const response = await fetch(url);
  const data = await response.json();

  return (
    data.items?.map((item) => ({
      value:
        item?.volumeInfo?.industryIdentifiers?.find((el) => el && el?.type === "ISBN_13")
          ?.identifier || item?.id,
      label: item?.volumeInfo?.title,
      imageUrl:
        (item?.volumeInfo?.imageLinks?.thumbnail &&
          replaceHttpWithHttps(item?.volumeInfo?.imageLinks?.thumbnail)) ||
        process.env.REACT_APP_SLS_IMAGE_CLDFRONT,
      authors: item?.volumeInfo?.authors,
    })) || []
  );
};

const BookSelector = ({ onSelect, isMobile }) => {
  const debouncedFetchBooks = useMemo(
    () =>
      debounce((inputValue, callback) => {
        fetchBooks(inputValue).then(callback);
      }, 300),
    []
  );

  const loadOptions = (inputValue, callback) => {
    debouncedFetchBooks(inputValue, callback);
  };

  const customStyles = {
    menu: (provided) => ({
      ...provided,
      zIndex: 12004,
    }),
    menuList: (provided) => ({
      ...provided,
      zIndex: 12004,
    }),
  };

  return (
    <AsyncSelect
      cacheOptions
      defaultOptions
      loadOptions={loadOptions}
      isClearable
      styles={customStyles}
      getOptionLabel={(option) => (
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            alignItems: "center",
          }}
        >
          <img
            src={option.imageUrl}
            alt={option.label}
            style={{ marginRight: "10px", width: "20px" }}
          />
          <span
            style={{
              fontWeight: "600",
              fontSize: `${isMobile ? "0.7rem" : "1rem"} `,
            }}
          >
            {option.label}
          </span>
          {option?.authors?.length > 0 && (
            <i
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: `${isMobile ? "0.7rem" : "1rem"}` }}>by</span>
              &nbsp;
              <span
                style={{
                  fontWeight: "bold",
                  fontSize: `${isMobile ? "0.7rem" : "1rem"}`,
                }}
              >
                {option?.authors?.join(", ")}
              </span>
            </i>
          )}
        </div>
      )}
      onChange={onSelect}
      placeholder={`Enter title`}
    />
  );
};

const BookReviewPage = ({ isIframe = false }) => {
  const { isMobile, router } = useAppContext();
  const [feedback, setFeedback] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const { partner, orim_source } = router.query || {};

  //Fetching UTM Params from common method
  const utmParams = getUTMParams(router.query);

  const handleSelect = (book) => {
    setSelectedBook(book);
  };

  const handleFeedbackClick = (e) => {
    setFeedback(e.target.value);
  };

  const signupLocation = isIframe ? "/book-review-booksweeps" : "/book-review";
  const slug = isIframe ? "/book-review-booksweeps" : "/book-review";

  return (
    <div
      className={[styles["book-review-page"], "book-review-page"].join(" ")}
      style={{
        "--newsletter-opacity": feedback ? "100%" : "10%",
        "--selected-book-label-fontsize": isMobile ? "1rem" : "2rem",
        "--selected-book-author-fontsize": isMobile ? "1rem" : "1.2rem",
        "--book-review-nl-info-fontsize": isMobile ? "0.7rem" : "",
      }}
    >
      <div
        className={`${styles["book-review-page-title"]} ${isIframe ? styles["book-review-page-title-hidden"] : ""}`}
      >
        Rate a book, get a book!
      </div>

      <div>
        <div className={styles["book-review-page-search-title"]}>
          What was the last book you read?
        </div>
        <div className={[styles["book-review-page-search"], "book-review-page-search"].join(" ")}>
          <BookSelector onSelect={handleSelect} isMobile={isMobile} />
        </div>
        {selectedBook ? (
          <div className={styles["selected-book-container"]}>
            <div>
              <img src={selectedBook.imageUrl} alt={selectedBook.label} width="100" />
            </div>
            <div>
              <div className={styles["selected-book-label"]}>{selectedBook.label}</div>
              <div className={styles["selected-book-author"]}>
                {selectedBook?.authors ? `by ${selectedBook?.authors?.join(", ")}` : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {selectedBook ? (
        <div>
          <div className={styles["book-review-page-feedback-title"]}>Did you enjoy it?</div>
          <div className={[styles["feedback__buttons"], "feedback__buttons"].join(" ")}>
            <div className={[styles["feedback-button"], "feedback-button", "love"].join(" ")}>
              <Button
                text={"Yes!"}
                value="love"
                handleClick={handleFeedbackClick}
                inlineStyles={feedback == "love" ? selectedStyle : unSelectedStyle}
              />
            </div>
            <div className={[styles["feedback-button"], "feedback-button", "hate"].join(" ")}>
              <Button
                text={"No, not really."}
                handleClick={handleFeedbackClick}
                value="hate"
                inlineStyles={feedback == "hate" ? selectedStyle : unSelectedStyle}
              />
            </div>
          </div>

          <div
            className={[styles["ldl-book-review-newsletter"], "ldl-book-review-newsletter"].join(
              " "
            )}
          >
            <NewsletterV2
              header={"Thanks! Enter your email address"}
              buttonText={"Get your free book"}
              skipPreferenceCenter={true}
              ldl={true}
              disabled={!feedback}
              sourceTitle={selectedBook?.value}
              lhRate={feedback}
              lhMedium={utmParams?.medium}
              inlineStyles={{
                button: {
                  backgroundColor: "#ff6f00",
                },
              }}
              additionalVars={{
                signup_location: signupLocation,
                slug: slug,
                referral_source: utmParams?.referral_source || "",
                lh_ad: "",
                lh_rate: feedback,
                lh_source: utmParams?.referral_source,
                lh_date: new Date(),
                lh_medium: utmParams?.medium,
                lh_source_title: selectedBook?.value,
                partner_checkbox: false,
                partner_checkbox_details: {
                  partner: partner || "",
                  partner_checkbox: false,
                  partner_tracking: {
                    orim_source: orim_source || "LDL-External",
                    orim_lh_historical: [{ lh_source_title: selectedBook?.value }],
                  },
                },
              }}
            />

            <div className={styles["book-review-nl-info"]}>
              {isIframe ? (
                <>
                  By entering your email address, you agree to receive emails from Early Bird Books,
                  a free daily newsletter filled with great deals on free and discounted ebooks.
                </>
              ) : (
                <>
                  By entering your email address, you agree to receive emails from Early Bird Books,
                  our free daily newsletter filled with great deals on free and discounted ebooks.
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BookReviewPage;
